import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, MATERIALS, BENNE_SIZES_ALL, allowedBenneSizes } from "@/lib/apiClient";
import { Share2, MessageCircle, Mail, FileDown, Link2, Check, ChevronDown, Copy } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BENNE_SIZES = BENNE_SIZES_ALL;

/**
 * Normalise un numéro français pour wa.me :
 * "06 12 34 56 78" → "33612345678"
 * "+33 6 12 ..."  → "33612..."
 * Tout autre format est retourné en chiffres uniquement.
 */
function normalizePhoneForWhatsApp(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("33")) return digits;
  if (digits.startsWith("0")) return "33" + digits.slice(1);
  return digits;
}

export default function SharePricingButtons({ user, client, label = "Envoyer à un client" }) {
  const [grid, setGrid] = useState(null);
  const [copied, setCopied] = useState(false);
  const [textCopied, setTextCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [emailMenuOpen, setEmailMenuOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const emailWrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (emailWrapRef.current && !emailWrapRef.current.contains(e.target)) {
        setEmailMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pricing");
        setGrid(data);
      } catch (err) {
        console.debug("pricing fetch failed:", err?.message);
      }
    })();
  }, []);

  const publicUrl = useMemo(() => {
    if (!grid?.share_token) return "";
    return `${window.location.origin}/tarifs/${grid.share_token}`;
  }, [grid?.share_token]);

  const buildText = useMemo(
    () => () => {
      if (!grid) return "";
      const lines = [];
      lines.push("🌱 FB ENVIRONNEMENT — GRILLE TARIFAIRE");
      lines.push("");
      if (client?.company_name) lines.push(`Pour : ${client.company_name}`);
      lines.push(`Commercial : ${user?.name || ""}`);
      if (user?.email) lines.push(`Contact : ${user.email}`);
      lines.push("━━━━━━━━━━━━━━━━━━━━");
      let hasAny = false;
      for (const m of MATERIALS) {
        const row = grid.prices[m];
        if (!row) continue;
        const ton = parseFloat(row.per_ton) || 0;
        const bennes = allowedBenneSizes(m).filter((s) => (parseFloat(row.per_benne?.[s]) || 0) > 0);
        if (ton === 0 && bennes.length === 0) continue;
        hasAny = true;
        lines.push("");
        lines.push(`*${m}*`);
        if (ton > 0) lines.push(`  • À la tonne : ${ton} €/t`);
        for (const s of bennes) {
          lines.push(`  • Benne ${s} m³ : ${row.per_benne[s]} €`);
        }
      }
      if (!hasAny) {
        lines.push("");
        lines.push("(Aucun tarif renseigné — pense à les enregistrer sur l'accueil)");
      }
      lines.push("");
      lines.push("━━━━━━━━━━━━━━━━━━━━");
      lines.push("Tarifs en ligne :");
      lines.push(publicUrl);
      return lines.join("\n");
    },
    [grid, user, client, publicUrl],
  );

  const phoneForWa = normalizePhoneForWhatsApp(client?.phone);
  const whatsappHref = useMemo(() => {
    const text = encodeURIComponent(buildText());
    return phoneForWa
      ? `https://wa.me/${phoneForWa}?text=${text}`
      : `https://wa.me/?text=${text}`;
  }, [buildText, phoneForWa]);

  const emailHref = useMemo(() => {
    const subject = encodeURIComponent("Grille tarifaire — FB Environnement");
    const body = encodeURIComponent(buildText());
    const to = client?.email ? encodeURIComponent(client.email) : "";
    return `mailto:${to}?subject=${subject}&body=${body}`;
  }, [buildText, client?.email]);

  const gmailHref = useMemo(() => {
    const subject = encodeURIComponent("Grille tarifaire — FB Environnement");
    const body = encodeURIComponent(buildText());
    const to = client?.email ? encodeURIComponent(client.email) : "";
    return `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${subject}&body=${body}`;
  }, [buildText, client?.email]);

  const outlookHref = useMemo(() => {
    const subject = encodeURIComponent("Grille tarifaire — FB Environnement");
    const body = encodeURIComponent(buildText());
    const to = client?.email ? encodeURIComponent(client.email) : "";
    return `https://outlook.live.com/mail/0/deeplink/compose?to=${to}&subject=${subject}&body=${body}`;
  }, [buildText, client?.email]);

  const copyEmailContent = useCallback(async () => {
    const subject = "Grille tarifaire — FB Environnement";
    const fullText = `Objet : ${subject}\n${client?.email ? `Destinataire : ${client.email}\n` : ""}\n${buildText()}`;
    try {
      await navigator.clipboard.writeText(fullText);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = fullText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  }, [buildText, client?.email]);

  const copyLink = useCallback(async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
    } catch {
      const input = document.createElement("input");
      input.value = publicUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [publicUrl]);

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(buildText());
    } catch {
      const ta = document.createElement("textarea");
      ta.value = buildText();
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setTextCopied(true);
    setTimeout(() => setTextCopied(false), 2000);
  };

  const exportPDF = () => {
    if (!grid) return;
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    doc.setFillColor(16, 185, 129);
    doc.rect(0, 0, 297, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("FB ENVIRONNEMENT", 14, 11);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Grille tarifaire — Tarifs en € HT", 14, 17);

    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    if (client?.company_name) doc.text(`Pour : ${client.company_name}`, 200, 11);
    doc.text(`Commercial : ${user?.name || ""}`, 200, client?.company_name ? 17 : 11);

    const head = [["Matière", "€ HT/tonne", ...BENNE_SIZES.map((s) => `${s} m³`)]];
    const body = MATERIALS.map((m) => {
      const row = grid.prices[m] || {};
      const ton = parseFloat(row.per_ton) || 0;
      const allowed = allowedBenneSizes(m);
      return [
        m,
        ton > 0 ? `${ton} €` : "—",
        ...BENNE_SIZES.map((s) => {
          if (!allowed.includes(s)) return "—";
          const v = parseFloat(row.per_benne?.[s]) || 0;
          return v > 0 ? `${v} €` : "—";
        }),
      ];
    });

    autoTable(doc, {
      head,
      body,
      startY: 28,
      styles: { fontSize: 9, halign: "center" },
      headStyles: { fillColor: [28, 25, 23], textColor: 255, fontStyle: "bold" },
      columnStyles: { 0: { halign: "left", fontStyle: "bold", cellWidth: 38 } },
      alternateRowStyles: { fillColor: [250, 250, 249] },
    });

    const finalY = doc.lastAutoTable?.finalY || 40;
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(`Tarifs en ligne : ${publicUrl}`, 14, finalY + 10);
    doc.text(
      `Édité le ${new Date().toLocaleDateString("fr-FR")} — Tarifs en € HT, indicatifs, valables sous réserve d'acceptation.`,
      14,
      finalY + 15,
    );

    const filename = client?.company_name
      ? `Tarifs-${client.company_name.replace(/\s+/g, "-")}.pdf`
      : `Tarifs-FB-Environnement.pdf`;
    doc.save(filename);
  };

  return (
    <div data-testid="share-pricing">
      <div className="flex items-center gap-2 mb-3">
        <Share2 className="w-4 h-4 text-stone-600" strokeWidth={2.5} />
        <span className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">
          {label}
        </span>
      </div>

      {client && (
        <div className="text-xs text-stone-500 mb-3">
          {phoneForWa ? (
            <span className="inline-flex items-center gap-1">
              <Check className="w-3.5 h-3.5 text-emerald-600" strokeWidth={3} />
              WhatsApp pré-rempli au <span className="font-bold">{client.phone}</span>
            </span>
          ) : (
            <span className="text-amber-600">{"Aucun téléphone — WhatsApp s'ouvrira sans destinataire."}</span>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-whatsapp"
          className="flex items-center justify-center gap-2 font-bold px-4 py-3 rounded-full transition-all active:scale-95 shadow-lg bg-[#25D366] hover:bg-[#1ebe5b] text-white shadow-[#25D366]/30"
        >
          <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
          <span className="text-sm">WhatsApp</span>
        </a>
        <div className="relative" ref={emailWrapRef}>
          <button
            type="button"
            onClick={() => setEmailMenuOpen((o) => !o)}
            data-testid="share-email"
            className="w-full flex items-center justify-center gap-2 font-bold px-4 py-3 rounded-full transition-all active:scale-95 shadow-lg bg-sky-500 hover:bg-sky-600 text-white shadow-sky-500/30"
          >
            <Mail className="w-5 h-5" strokeWidth={2.5} />
            <span className="text-sm">Email</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${emailMenuOpen ? "rotate-180" : ""}`} strokeWidth={3} />
          </button>
          {emailMenuOpen && (
            <div
              data-testid="share-email-menu"
              className="absolute z-30 top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-white border border-stone-200 rounded-2xl shadow-2xl overflow-hidden"
            >
              <a
                href={gmailHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setEmailMenuOpen(false)}
                data-testid="share-email-gmail"
                className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-stone-800 hover:bg-stone-50 transition-colors"
              >
                <span className="w-7 h-7 rounded-full bg-red-50 text-red-600 grid place-items-center text-base font-black">G</span>
                Gmail (web)
              </a>
              <a
                href={outlookHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setEmailMenuOpen(false)}
                data-testid="share-email-outlook"
                className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-stone-800 hover:bg-stone-50 transition-colors border-t border-stone-100"
              >
                <span className="w-7 h-7 rounded-full bg-sky-50 text-sky-700 grid place-items-center text-base font-black">O</span>
                Outlook (web)
              </a>
              <a
                href={emailHref}
                onClick={() => setEmailMenuOpen(false)}
                data-testid="share-email-mailto"
                className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-stone-800 hover:bg-stone-50 transition-colors border-t border-stone-100"
              >
                <Mail className="w-5 h-5 text-stone-500" strokeWidth={2.5} />
                Mail de l&apos;appareil
              </a>
              <button
                type="button"
                onClick={() => {
                  copyEmailContent();
                  setEmailMenuOpen(false);
                }}
                data-testid="share-email-copy"
                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-stone-800 hover:bg-stone-50 transition-colors border-t border-stone-100"
              >
                {emailCopied ? (
                  <Check className="w-5 h-5 text-emerald-600" strokeWidth={3} />
                ) : (
                  <Copy className="w-5 h-5 text-stone-500" strokeWidth={2.5} />
                )}
                {emailCopied ? "Copié !" : "Copier le message"}
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={exportPDF}
          data-testid="share-pdf"
          className="flex items-center justify-center gap-2 font-bold px-4 py-3 rounded-full transition-all active:scale-95 shadow-lg bg-rose-500 hover:bg-rose-600 text-white shadow-rose-500/30"
        >
          <FileDown className="w-5 h-5" strokeWidth={2.5} />
          <span className="text-sm">PDF</span>
        </button>
        <button
          type="button"
          onClick={copyLink}
          data-testid="share-link"
          className="flex items-center justify-center gap-2 font-bold px-4 py-3 rounded-full transition-all active:scale-95 shadow-lg bg-stone-900 hover:bg-stone-800 text-white shadow-stone-900/20"
        >
          {copied ? <Check className="w-5 h-5" strokeWidth={3} /> : <Link2 className="w-5 h-5" strokeWidth={2.5} />}
          <span className="text-sm">{copied ? "Copié !" : "Copier le lien"}</span>
        </button>
      </div>

      <button
        type="button"
        onClick={() => setShowPreview((v) => !v)}
        data-testid="preview-toggle"
        className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-stone-500 hover:text-stone-900 transition-colors"
      >
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${showPreview ? "rotate-180" : ""}`}
          strokeWidth={3}
        />
        {showPreview ? "Masquer le texte" : "Voir / copier le texte envoyé"}
      </button>
      {showPreview && (
        <div className="mt-2 bg-stone-50 border border-stone-200 rounded-2xl p-3">
          <pre
            data-testid="share-preview"
            className="whitespace-pre-wrap text-xs text-stone-700 font-mono leading-relaxed max-h-64 overflow-y-auto"
          >
            {buildText()}
          </pre>
          <button
            type="button"
            onClick={copyText}
            data-testid="copy-text"
            className="mt-2 inline-flex items-center gap-1.5 bg-stone-900 hover:bg-stone-800 text-white font-bold px-3 py-1.5 rounded-full text-xs active:scale-95"
          >
            {textCopied ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : <Link2 className="w-3.5 h-3.5" strokeWidth={2.5} />}
            {textCopied ? "Texte copié !" : "Copier ce texte"}
          </button>
        </div>
      )}
    </div>
  );
}
